import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {CreatedWarehouseLogin} from 'constants/WarehouseConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

/** @package */
export interface CreateWarehouseLoginModalProps {
  createLoginModal: {
    creating: boolean;
    createdLogin: CreatedWarehouseLogin | null;
    label: string;
  };
  onCreate: (label: string) => void;
  onLabelChange: (newLabel: string) => void;
  onRequestClose: () => void;
}

export default function CreateWarehouseLoginModal({
  createLoginModal,
  onCreate,
  onLabelChange,
  onRequestClose,
}: CreateWarehouseLoginModalProps): JSX.Element {
  let button;
  const {createdLogin} = createLoginModal;
  if (createLoginModal.creating) {
    button = {key: 'create', text: 'Creating...', disabled: true, submit: true};
  } else if (createdLogin) {
    button = {key: 'create', text: 'Done', cancel: true};
  } else {
    button = {
      key: 'create',
      text: 'Create',
      onClick: () => {
        onCreate(createLoginModal.label);
      },
      submit: true,
    };
  }
  return (
    <Modal
      title="Create credentials"
      onRequestClose={onRequestClose}
      closeable={!createLoginModal.creating}
      buttons={[button]}
      analyticsModule={ANALYTICS_MODULE.DATA_MANAGEMENT}
      analyticsModalType="create_warehouse_login"
    >
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Label (optional)</Label>
            <FormInput
              placeholder=""
              value={createLoginModal.label}
              onChange={(e) => {
                onLabelChange(e.target.value);
              }}
              readOnly={createLoginModal.creating || createdLogin != null}
              {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'labelInput'})}
            />
          </FormGroup>
        </Col>
      </Row>
      {createdLogin && (
        <>
          <Row>
            <Col md={12}>
              <div>
                <BenchlingIcon icon="done" className="u-success-color" /> Credentials created!
              </div>
              <div className="u-spacing">
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text={
                    <>
                      Make sure you store the password safely <em>now</em>, as it cannot be retrieved later.
                    </>
                  }
                  supportingInfo={
                    <span>
                      For connection instructions and <em>important</em> security considerations,{' '}
                      <a
                        href="https://docs.benchling.com/docs/getting-started"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        please see our guide
                      </a>{' '}
                      (opens in new tab).
                    </span>
                  }
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>JDBC &amp; PostgreSQL Connection String</Label>
                <FormInput
                  readOnly={true}
                  selectAllOnClick={true}
                  value={`postgresql://${createdLogin.username}@${createdLogin.pgConnectParams.host}:${createdLogin.pgConnectParams.port}/${createdLogin.pgConnectParams.dbname}?sslmode=${createdLogin.pgConnectParams.sslmode}`}
                  {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'jdbcInput'})}
                />
                <div className="u-tiny-spacing-top">
                  The CA Certificate can be downloaded{' '}
                  <a
                    href="https://truststore.pki.rds.amazonaws.com/global/global-bundle.pem"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  . Be sure to wrap the connection string in single-quotes for <code>psql</code> and other
                  shell commands!
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Username</Label>
                <FormInput
                  readOnly={true}
                  selectAllOnClick={true}
                  value={createdLogin.username}
                  {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'usernameInput'})}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Password</Label>
                <FormInput
                  readOnly={true}
                  selectAllOnClick={true}
                  value={createdLogin.password}
                  {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'passwordInput'})}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Hostname &amp; Port</Label>
                <FormInput
                  readOnly={true}
                  selectAllOnClick={true}
                  value={`${createdLogin.pgConnectParams.host}:${createdLogin.pgConnectParams.port}`}
                  {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'hostnameAndPortInput'})}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Database Name</Label>
                <FormInput
                  readOnly={true}
                  selectAllOnClick={true}
                  value={createdLogin.pgConnectParams.dbname}
                  {...getTestProps({component: 'CreateWarehouseLoginModal', element: 'dbNameInput'})}
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
}
